/**
 * What the account formula-edit screen reads and writes (ACC-003, ACC-004): the current mix,
 * optional ingredients the customer may remove, vanilla or chocolate, and the protein bases the
 * pharmacy can make. Flavor and protein apply without a physician; a removal goes to review.
 *
 * Type aliases rather than interfaces so the Worker's `json()` accepts the payload: it takes a JSON
 * value, and only an alias is assignable to an index-signature type.
 */
import { z } from "zod";

export const ACCOUNT_FORMULA_FLAVORS = ["vanilla", "chocolate"] as const;

export type AccountFormulaEditFlavor = (typeof ACCOUNT_FORMULA_FLAVORS)[number];

export function isAccountFormulaFlavor(value: string): value is AccountFormulaEditFlavor {
  return ACCOUNT_FORMULA_FLAVORS.some((flavor) => flavor === value);
}

export type AccountFormulaEditStatus = "draft" | "in_review" | "approved";

export type AccountFormulaEditIngredient = {
  readonly ingredientId: string;
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
  readonly reason: string;
  readonly isProteinBase: boolean;
};

export type AccountFormulaProteinOption = {
  readonly ingredientId: string;
  readonly name: string;
};

export type AccountFormulaRestorable = {
  readonly ingredientId: string;
  readonly name: string;
};

export type AccountFormulaEdit = {
  readonly formulaVersionId: string;
  readonly status: AccountFormulaEditStatus;
  readonly flavor: AccountFormulaEditFlavor | null;
  readonly proteinBaseIngredientId: string | null;
  readonly ingredients: readonly AccountFormulaEditIngredient[];
  readonly proteinOptions: readonly AccountFormulaProteinOption[];
  readonly restorable: readonly AccountFormulaRestorable[];
  /** True when this version is a customer removal waiting for a physician (ACC-003). */
  readonly reviewPending: boolean;
};

const Flavor = z.enum(["vanilla", "chocolate"]);

const Status = z.enum(["draft", "in_review", "approved"]);

const Ingredient = z.object({
  ingredientId: z.string(),
  name: z.string(),
  dose: z.number(),
  unit: z.string(),
  reason: z.string(),
  isProteinBase: z.boolean(),
});

const Named = z.object({
  ingredientId: z.string(),
  name: z.string(),
});

const Edit = z.object({
  formulaVersionId: z.string(),
  status: Status,
  flavor: Flavor.nullable(),
  proteinBaseIngredientId: z.string().nullable(),
  ingredients: z.array(Ingredient),
  proteinOptions: z.array(Named),
  restorable: z.array(Named),
  reviewPending: z.boolean(),
});

/** The formula-edit payload from a JSON body, or null when it is not one. */
export function readAccountFormulaEdit(text: string): AccountFormulaEdit | null {
  try {
    const parsed = Edit.safeParse(JSON.parse(text));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
